import type { SurplusChartDto } from "@gladis/shared";
import { StackedBarChart, type BarBucket, type BarSeries } from "@gladis/ui";
import { useT, type TranslateFn } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { SectionCard } from "../section-card.js";
import { formatMonthOnly } from "@gladis/ui/i18n";

/**
 * ④ The Surplus History Chart — whether "we kept €2,320" is normal or is this month.
 *
 * ==========================================================================================
 * ONE SERIES, AND THE BAR CHART IS THE RIGHT SHAPE FOR A REASON
 * ==========================================================================================
 *
 * `Left` is a figure per PERIOD with nothing meaningful between two months, which is what a bucket is —
 * `bars.ts` makes the distinction against the area chart's dated points. An area here would draw a
 * continuous line through twelve discrete answers and invite reading a value off the middle of March.
 *
 * A deficit month draws BELOW THE AXIS at full weight, and `StackedBarChartImpl` gained a zero
 * `ReferenceLine` in this feature for it — drawn only when a bucket is negative, so income's chart is
 * untouched (T010, T011). Recharts already positioned a negative bar correctly; what was missing was the
 * line it hangs from, without which the reader has nothing to measure the drop against.
 *
 * ==========================================================================================
 * THE WINDOW IS THE CHART'S OWN, AND THE LABEL SAYS SO
 * ==========================================================================================
 *
 * A rolling twelve months, which does NOT follow the health card's period filter — so these bars do not
 * sum to the card's "this year", and that is two windows answering two questions rather than an
 * inconsistency (AC-D22a). The heading states the window, because a chart beneath a card with a period
 * control will otherwise be read as obeying it.
 */

/** Cents-as-string → euros, which is the unit the primitive formats and plots. */
function euros(cents: string): number {
  return Number(cents) / 100;
}

/**
 * The one series. Keyed, because `BarBucket.values` is a map from series key to figure.
 *
 * BUILT IN THE COMPONENT, not at module scope — feature 021. A `t()` evaluated at import freezes the
 * label in whichever language loaded first, and this one is drawn on an axis where a stale word is
 * particularly easy to miss.
 */
function seriesFor(t: TranslateFn): BarSeries[] {
  return [{ key: "left", label: t("dash.health.left") }];
}

/**
 * `Sep`, with the year where it changes — income's own rule, and its reasoning applies unchanged.
 *
 * "Thirty-six bars with the year on every one is noise; without it entirely, a reader cannot tell which
 * January is which." Twelve months spanning two years is the same problem at a smaller scale, and
 * `axisLabel` from `@gladis/ui` renders "Sep 2025" for every bucket — correct, and twelve of them do not
 * fit at a phone's width.
 */
function bucketLabel(key: string, previous: string | undefined): string {
  const [year = "", month = ""] = key.split("-");
  const name = formatMonthOnly(Number(year), Number(month));
  return previous === undefined || previous.slice(0, 4) !== year ? `${name} ${year}` : name;
}

export function SurplusChart({ chart }: { chart: SurplusChartDto }): ReactNode {
  const t = useT();
  const buckets: BarBucket[] = chart.buckets.map((bucket, index) => ({
    key: bucket.key,
    label: bucketLabel(bucket.key, chart.buckets[index - 1]?.key),
    partial: bucket.partial,
    values: { left: euros(bucket.leftCents) },
    /*
     * NO `projected`, and its absence is the requirement rather than an omission (FR-063). The primitive
     * draws one faded above the solid part and income's chart uses it for the current year; projecting
     * the rest of this month would be forecasting, which is a stated non-goal. `partial` says the month
     * is still running and stops there.
     */
  }));

  return (
    /*
      The heading states the WINDOW as well as the subject, and that is load-bearing rather than verbose:
      these bars do not follow the page's filters (AC-D22a), and since those filters moved into the title
      row on 2026-08-14 they look page-wide. A chart that does not obey a control the reader takes to be
      page-wide has to say so itself.
    */
    <SectionCard
      title={t("dash.surplus.heading")}
      hint={t("dash.surplus.hint")}
      testId="surplus-chart"
    >
      <StackedBarChart
        series={seriesFor(t)}
        buckets={buckets}
        emptyMessage={t("dash.surplus.empty")}
      />
    </SectionCard>
  );
}
